import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Observable } from 'rxjs';
import { MatSelectionListChange } from '@angular/material/list';
import { startWith, map } from 'rxjs/operators';
import { DeleteDialogComponent } from '../../misc-components/delete-dialog/delete-dialog.component';
import { RoleService } from 'src/app/services/role.service';
import {AVAILABLE_PERMISSIONS	} from '../../../services/permissions'
import { MatSelectionList } from '@angular/material/list';
import { trigger, transition, style, animate } from '@angular/animations';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-role-editor',
  templateUrl: './role-editor.component.html',
  styleUrls: ['./role-editor.component.css'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms', style({ opacity: 1 }))
      ])
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateY(-10px)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class RoleEditorComponent implements OnInit {
  @ViewChild('termInput') termInput: ElementRef<HTMLInputElement>;
  @ViewChild('permissionList') permissionList: MatSelectionList;

  canViewRoles: boolean = false;
  canChangeRoles: boolean = false;
  id: string;
  name: string = '';
  terms: string[] = [];
  newUser: boolean = false;
  isSuperAdmin: boolean = false;

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly availableRoles = AVAILABLE_PERMISSIONS;

  termCtrl = new FormControl('');
  filteredTerms: Observable<string[]>;

  removable: boolean = true;
  addOnBlur: boolean = false;

  constructor(
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private roleService: RoleService,
    private authService: AuthService
  ) {
    this.setupFilteredTerms();
  }


  ngOnInit(): void {
    try {
      // Get the ID from route params
      this.id = this.activatedRoute.snapshot.params['id'];
      this.newUser = !this.id || this.id === 'new';

      const userPermissions = this.authService.getPermissions();
      //console.log('Raw user permissions:', userPermissions);

      // Check permissions
      this.canViewRoles = userPermissions.includes('canViewRoles');
      this.canChangeRoles = userPermissions.includes('canChangeRoles');

      //console.log('Permission check results:', {
      //  canViewRoles: this.canViewRoles,
      //  canChangeRoles: this.canChangeRoles,
      //  roleId: this.id,
      //  isNewUser: this.newUser
      //});

      if (!this.canViewRoles) {
        this.router.navigate(['/unauthorized']);
        return;
      }

      if (!this.newUser) {
        this.loadExistingRole();
      }
    } catch (error) {
      console.error('Error during initialization:', error);
      this.router.navigate(['/unauthorized']);
    }
  }
  private setupFilteredTerms(): void {
    this.filteredTerms = this.termCtrl.valueChanges.pipe(
      startWith(null),
      map(term => this.filterTerms(term))
    );
  }

  private filterTerms(term: string | null): string[] {
    if (!term) {
      return this.availableRoles.filter(role => !this.terms.includes(role));
    }

    const filterValue = term.toLowerCase();
    return this.availableRoles.filter(role =>
      role.toLowerCase().includes(filterValue) && !this.terms.includes(role)
    );
  }

  private async loadExistingRole(): Promise<void> {
    try {
      //console.log('Loading role with ID:', this.id);
      const role: any = await this.roleService.getRole(this.id);
      //console.log('Loaded role:', role);

      this.name = role.name;

      if (this.name === 'superadmin') {
        this.isSuperAdmin = true;
        this.removable = false;
      }

      // Extract permissions that are set to true
      this.terms = Object.entries(role)
        .filter(([key, value]) =>
          key &&
          key !== 'name' &&
          key !== 'id' &&
          value === true &&
          AVAILABLE_PERMISSIONS.includes(key as any)
        )
        .map(([key]) => key);

      //console.log('Extracted permissions:', this.terms);
    } catch (error) {
      console.error('Error loading role:', error);
    }
  }

  async save(): Promise<void> {
    try {
      if (!this.name.trim()) {
        console.error('Role name cannot be empty');
        return;
      }

      // Get selected permissions using MatSelectionList
      const selectedPermissions = this.permissionList.selectedOptions.selected
        .map(option => option.value);

      console.log('Current selected permissions:', selectedPermissions);

      // Create payload object with all permissions set to false first
      const payload: any = {
        name: this.name.trim(),
      };

      // Set all permissions to false initially
      this.availableRoles.forEach(permission => {
        payload[permission] = false;
      });

      // Set selected permissions to true
      selectedPermissions.forEach(permission => {
        payload[permission] = true;
      });

      console.log('Saving role with payload:', payload);

      let result;
      if (this.newUser) {
        result = await this.roleService.createRole(this.name, selectedPermissions);
      } else {
        // Make sure we have an ID for update
        if (!this.id) {
          console.error('No role ID found for update');
          return;
        }
        result = await this.roleService.updateRole(this.name, selectedPermissions, this.id);
        console.log('Update result:', result);
      }

      // Force permissions refresh
      await this.authService.refreshPermissions();

      // Add delay before navigation
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.router.navigateByUrl('/roleManagement');
    } catch (error) {
      console.error('Error saving role:', error);
    }
  }

  async deleteRole(): Promise<void> {
    const dialogRef = this.dialog.open(DeleteDialogComponent);

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        try {
          await this.roleService.deleteRole(this.id);
          setTimeout(() => this.router.navigate(['/roleManagement']), 1000);
        } catch (error) {
          console.error('Error deleting role:', error);
        }
      }
    });
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.terms.push(value);
    }

    event.chipInput?.clear();
    this.termCtrl.setValue(null);
  }

  remove(term: string): void {
    const index = this.terms.indexOf(term);
    if (index >= 0) {
      this.terms.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.terms.push(event.option.value);
    this.termInput.nativeElement.value = '';
    this.termCtrl.setValue(null);
  }
  onPermissionToggle(event: MatSelectionListChange): void {
    const permission = event.options[0].value;
    const isSelected = event.options[0].selected;

    console.log(`Toggle ${permission}: ${isSelected}`);

    if (isSelected) {
      if (!this.terms.includes(permission)) {
        this.terms = [...this.terms, permission];
        console.log('Added permission:', permission);
      }
    } else {
      this.terms = this.terms.filter(term => term !== permission);
      console.log('Removed permission:', permission);
    }

    console.log('Updated permissions:', this.terms);
  }

  formatPermissionLabel(permission: string): string {
    return permission
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace('can ', '');
  }

  getPermissionDescription(permission: string): string {
    const descriptions: { [key: string]: string } = {
      canSearch: 'Allow user to search through entries',
      canCreateEntries: 'Allow user to create new entries',
      canUpdateEntries: 'Allow user to modify existing entries',
      canMarkEntriesChecked: 'Allow user to mark entries as checked',
      canMarkEntriesConfirmed: 'Allow user to mark entries as confirmed',
      canViewUsers: 'Allow user to view user list',
      canCreateUsers: 'Allow user to create new users',
      canCreateMappings: 'Allow user to create data mappings',
      canViewBacklogs: 'Allow user to view system backlogs',
      canAccessAnalyzer: 'Allow user to access the analyzer tool',
      canCreateTags: 'Allow user to create new tags',
      canViewDashboard: 'Allow user to view the dashboard',
      canAccessVideoDownloader: 'Allow user to access video downloader',
      canAddEntryToReport: 'Allow user to add entries to reports',
      canViewOwnEntries: 'Allow user to view their own entries',
      canDeleteNewsEntries: 'Allow user to delete news entries',
      canManageRoles: 'Allow user to manage roles',
      canTranslateNewsEntry: 'Allow user to translate news entries',
      canViewStatsAndGraphs: 'Allow user to view statistics and graphs',
      canAccessSiteManagement: 'Allow user to access site management',
      canViewRoles: 'Allow user to view roles',
      canChangeRoles: 'Allow user to change roles',
    };

    return descriptions[permission] || 'No description available';
  }
}